import type {
  VisualPayload,
  SeasonOverview,
  CupSlot,
} from "../../types";
import CardShell from "./_CardShell";
import {
  SeasonHeaderStrip,
  SeasonSourceLink,
  CupWeekBlock,
  groupCupsByWeek,
  parseCupRules,
  TYPE_CHIP_CLASSES,
} from "./_seasonShared";

interface Props { payload: VisualPayload }

/**
 * "Cups running today" — date-filtered subset of the season's cup
 * rotation.  Each currently-active cup is rendered with its parsed
 * type chips + CP cap + (for plain Great/Ultra/Master) just a league
 * badge.  Multi-cup days (common in Forever Forward — e.g. the
 * "all three leagues" weeks) all render in the same block.
 *
 * Phase 3 — "state of today" intents.
 */
export default function CupsTodayCard({ payload }: Props) {
  const sections = payload.sections ?? [];
  const overview = (sections.find((s) => s.section === "season_overview") ?? {}) as SeasonOverview;
  const cupScheduleSec = sections.find((s) => s.section === "cup_schedule");
  const cups = (cupScheduleSec?.items as CupSlot[] | undefined) ?? [];

  // Pre-season fallback: when no cups are running today AND the season
  // is upcoming, the backend ships a "next_up" section with the first
  // cup in the rotation so the user gets a useful "season starts X,
  // first cup is Y" answer.
  const nextUpSec = sections.find((s) => s.section === "next_up");
  const nextUpCups = (nextUpSec?.items as CupSlot[] | undefined) ?? [];

  if (cups.length === 0) {
    return (
      <CardShell title={payload.title} source={payload.data_source} timestamp={payload.data_timestamp} sourceUrl={payload.source_url} shareUrl={payload.share_url} snapshotPayload={payload.snapshot_eligible ? payload : undefined} accent="border-emerald-500/30">
        <SeasonHeaderStrip overview={overview} />
        <p className="text-sm text-white/55 py-2">
          {overview.is_upcoming
            ? `No cups running yet — ${overview.season_name ?? "the season"} starts soon.`
            : "No GBL cups are running today — the current season has rest days between cup rotations."}
        </p>
        {nextUpCups.length > 0 && (
          <div className="mt-3">
            <p className="text-[11px] font-semibold uppercase tracking-widest text-white/40 mb-2">
              First cup in the rotation
            </p>
            <ul className="space-y-0">
              {groupCupsByWeek(nextUpCups).map((w, i) => (
                <CupWeekBlock key={i} week={w} />
              ))}
            </ul>
          </div>
        )}
        <SeasonSourceLink sourceUrl={payload.source_url} />
      </CardShell>
    );
  }

  // Cups today share the same date range (today falls in one week
  // window).  groupCupsByWeek collapses them under a single date
  // header so the user sees "Today: 3 cups" rather than 3 separate
  // identical-date rows.
  const cupWeeks = groupCupsByWeek(cups);
  const liveBadge = { label: "live now", cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/25" };

  return (
    <CardShell
      title={payload.title}
      source={payload.data_source}
      timestamp={payload.data_timestamp}
      sourceUrl={payload.source_url}
      shareUrl={payload.share_url}
      snapshotPayload={payload.snapshot_eligible ? payload : undefined}
      accent="border-emerald-500/30"
    >
      <SeasonHeaderStrip overview={overview} />

      {/* For each active cup, surface its parsed restrictions
          (CP cap + type chips) when available.  Mega Edition / plain
          standard leagues won't have rules_summary; they just show
          the league badge from the CupWeekBlock fallback. */}
      <div className="space-y-3">
        {cupWeeks.map((w, wi) => (
          <div key={wi}>
            {/* The CupWeekBlock renders the date + nested cup rows.
                We render parsed-restrictions chips per cup below
                that, so the user sees both the headline AND the
                actual rules in the same scan. */}
            <ul className="space-y-0">
              <CupWeekBlock week={w} badge={liveBadge} />
            </ul>

            {/* Per-cup parsed restrictions */}
            {w.cups.map((c, ci) => {
              const parsed = parseCupRules(c.rules_summary);
              const hasParsed =
                parsed.cp_cap !== null
                || parsed.eligible_types.length > 0
                || parsed.banned_pokemon.length > 0;
              if (!hasParsed) return null;
              return (
                <div key={ci} className="pl-5 mt-1.5 mb-1 space-y-1">
                  <p className="text-[10px] uppercase tracking-widest text-white/35">
                    {c.cup_name?.split(":")[0] ?? c.cup_name} — restrictions
                  </p>
                  <div className="flex flex-wrap items-center gap-1.5">
                    {parsed.cp_cap !== null && (
                      <span className="text-[11px] px-2 py-0.5 rounded-full bg-white/10 text-white/85 font-medium tabular-nums">
                        {parsed.cp_cap.toLocaleString()} CP
                      </span>
                    )}
                    {parsed.eligible_types.map((t) => (
                      <span
                        key={t}
                        className={`text-[11px] px-2 py-0.5 rounded-full border capitalize ${
                          TYPE_CHIP_CLASSES[t] ?? "bg-white/10 text-white/70 border-white/15"
                        }`}
                      >
                        {t}
                      </span>
                    ))}
                  </div>
                  {parsed.banned_pokemon.length > 0 && (
                    <p className="text-[11px] text-white/55 leading-relaxed">
                      <span className="text-white/40">Banned:</span> {parsed.banned_pokemon.join(", ")}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </div>

      <SeasonSourceLink sourceUrl={payload.source_url} />
    </CardShell>
  );
}
